import React, { useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FaSearch, FaMapMarkerAlt, FaBriefcase, FaFilter, FaBookmark, FaRegBookmark, FaDollarSign } from 'react-icons/fa';

const JobBoardPage = () => {
  // Extended job data for multiple pages
  const allJobs = [
    // Page 1
    {
      id: 1,
      title: 'Senior Frontend Developer',
      company: 'TechCorp Inc.',
      location: 'San Francisco, CA',
      type: 'Full-time',
      salary: '$120k - $150k',
      description: 'We are looking for an experienced Frontend Developer with strong React skills to join our growing team.',
      posted: '2 days ago',
      logo: 'https://via.placeholder.com/60',
      tags: ['React', 'TypeScript', 'Redux', 'CSS'],
      saved: false
    },
    {
      id: 2,
      title: 'Backend Engineer',
      company: 'DataSoft Solutions',
      location: 'Remote',
      type: 'Full-time',
      salary: '$100k - $130k',
      description: 'Join our backend team to build scalable services that power our platform.',
      posted: '1 week ago',
      logo: 'https://via.placeholder.com/60',
      tags: ['Node.js', 'Express', 'MongoDB', 'AWS'],
      saved: true
    },
    {
      id: 3,
      title: 'UX/UI Designer',
      company: 'Creative Studio',
      location: 'New York, NY',
      type: 'Contract',
      salary: '$75 - $95/hr',
      description: 'We need a talented designer to create stunning user experiences for our clients.',
      posted: '3 days ago',
      logo: 'https://via.placeholder.com/60',
      tags: ['Figma', 'Sketch', 'Adobe XD', 'UI Design'],
      saved: false
    },
    // Page 2
    {
      id: 4,
      title: 'DevOps Engineer',
      company: 'CloudTech Services',
      location: 'Boston, MA',
      type: 'Full-time',
      salary: '$110k - $140k',
      description: 'Help us build and maintain our cloud infrastructure and CI/CD pipelines.',
      posted: '5 days ago',
      logo: 'https://via.placeholder.com/60',
      tags: ['Docker', 'Kubernetes', 'AWS', 'CI/CD'],
      saved: false
    },
    {
      id: 5,
      title: 'Mobile Developer',
      company: 'AppWorks Inc.',
      location: 'Austin, TX',
      type: 'Full-time',
      salary: '$95k - $125k',
      description: 'Develop native mobile applications for iOS and Android platforms.',
      posted: 'Just now',
      logo: 'https://via.placeholder.com/60',
      tags: ['Swift', 'Kotlin', 'React Native', 'Firebase'],
      saved: false
    },
    {
      id: 6,
      title: 'Data Scientist',
      company: 'Analytics Pro',
      location: 'Chicago, IL',
      type: 'Full-time',
      salary: '$130k - $160k',
      description: 'Apply statistical analysis and machine learning to extract insights from our data.',
      posted: '1 week ago',
      logo: 'https://via.placeholder.com/60',
      tags: ['Python', 'R', 'Machine Learning', 'SQL'],
      saved: false
    },
    // Page 3
    {
      id: 7,
      title: 'Product Manager',
      company: 'InnovateTech',
      location: 'Seattle, WA',
      type: 'Full-time',
      salary: '$115k - $145k',
      description: 'Lead product development from concept to launch working with cross-functional teams.',
      posted: '3 days ago',
      logo: 'https://via.placeholder.com/60',
      tags: ['Product Strategy', 'Agile', 'User Research', 'Roadmapping'],
      saved: false
    },
    {
      id: 8,
      title: 'QA Engineer',
      company: 'Quality Systems',
      location: 'Denver, CO',
      type: 'Full-time',
      salary: '$90k - $110k',
      description: 'Ensure our products meet the highest quality standards through manual and automated testing.',
      posted: '6 days ago',
      logo: 'https://via.placeholder.com/60',
      tags: ['Selenium', 'Jest', 'Cypress', 'TestRail'],
      saved: false
    },
    {
      id: 9,
      title: 'Technical Writer',
      company: 'Documentation Inc.',
      location: 'Remote',
      type: 'Contract',
      salary: '$40 - $60/hr',
      description: 'Create clear, concise documentation for our technical products and APIs.',
      posted: '2 weeks ago',
      logo: 'https://via.placeholder.com/60',
      tags: ['API Documentation', 'Technical Writing', 'Markdown', 'Information Architecture'],
      saved: false
    }
  ];

  const [jobs, setJobs] = useState(allJobs);
  const [filters, setFilters] = useState({
    search: '',
    location: '',
    jobType: ''
  });

  const [currentPage, setCurrentPage] = useState(1);
  const jobsPerPage = 3; // Show 3 jobs per page

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleSaveJob = (id) => {
    setJobs(jobs.map(job => 
      job.id === id ? { ...job, saved: !job.saved } : job
    ));
  };

  const filteredJobs = jobs.filter(job => {
    return (
      job.title.toLowerCase().includes(filters.search.toLowerCase()) &&
      job.location.toLowerCase().includes(filters.location.toLowerCase()) &&
      (filters.jobType === '' || job.type === filters.jobType)
    );
  });

  // Pagination logic
  const indexOfLastJob = currentPage * jobsPerPage;
  const indexOfFirstJob = indexOfLastJob - jobsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);
  const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);

  const paginate = (pageNumber) => {
    // Scroll to top when changing pages
    window.scrollTo(0, 0);
    setCurrentPage(pageNumber);
  };

  return (
    <div className="container-fluid py-5 mt-5 bg-light min-vh-100">
      <div className="container">
        <div className="row mb-4">
          <div className="col-12">
            <h1 className="fw-bold text-primary text-center mb-3">Find Your Dream Job</h1>
            <p className="lead text-muted text-center">Discover the best job opportunities that match your skills and career goals</p>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body p-3">
                <form>
                  <div className="row g-3">
                    <div className="col-md-3">
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <FaSearch className="text-muted" />
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0"
                          placeholder="Job title, keywords, or company"
                          name="search"
                          value={filters.search}
                          onChange={handleFilterChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <FaMapMarkerAlt className="text-muted" />
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0"
                          placeholder="Location"
                          name="location"
                          value={filters.location}
                          onChange={handleFilterChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-2">
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <FaBriefcase className="text-muted" />
                        </span>
                        <select
                          className="form-select border-start-0"
                          name="jobType"
                          value={filters.jobType}
                          onChange={handleFilterChange}
                        >
                          <option value="">Job Type</option>
                          <option value="Full-time">Full-time</option>
                          <option value="Part-time">Part-time</option>
                          <option value="Contract">Contract</option>
                          <option value="Freelance">Freelance</option>
                          <option value="Internship">Internship</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <button type="submit" className="btn btn-primary d-flex w-75">
                        Search Jobs
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Pills & Count */}
        <div className="row mb-3">
          <div className="col-md-8">
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <span className="text-muted me-2">
                <FaFilter size={14} className="me-1" />
                Filters:
              </span>
              {filters.search && (
                <span className="badge bg-light text-dark border rounded-pill p-2">
                  {filters.search}
                  <button type="button" className="btn-close ms-2" aria-label="Close" onClick={() => handleFilterChange({ target: { name: 'search', value: '' } })}></button>
                </span>
              )}
              {filters.location && (
                <span className="badge bg-light text-dark border rounded-pill p-2">
                  {filters.location}
                  <button type="button" className="btn-close ms-2" aria-label="Close" onClick={() => handleFilterChange({ target: { name: 'location', value: '' } })}></button>
                </span>
              )}
              {filters.jobType && (
                <span className="badge bg-light text-dark border rounded-pill p-2">
                  {filters.jobType}
                  <button type="button" className="btn-close ms-2" aria-label="Close" onClick={() => handleFilterChange({ target: { name: 'jobType', value: '' } })}></button>
                </span>
              )}
            </div>
          </div>
          <div className="col-md-4 text-md-end mt-2 mt-md-0">
            <p className="mb-0 text-muted">
              Showing {Math.min(indexOfFirstJob + 1, filteredJobs.length)} - {Math.min(indexOfLastJob, filteredJobs.length)} of {filteredJobs.length} jobs
            </p>
          </div>
        </div>

        {/* Job Listings */}
        <div className="row">
          <div className="col-12">
            {currentJobs.length > 0 ? (
              currentJobs.map(job => (
                <div key={job.id} className="card mb-3 border-0 shadow-sm">
                  <div className="card-body p-4">
                    {/* Job Info and Details */}
                    <div className="row">
                      <div className="col-lg-8">
                        <div className="d-flex">
                          <div className="me-3">
                            <img src={job.logo} alt={job.company} className="img-fluid rounded" style={{ width: '60px', height: '60px' }} />
                          </div>
                          <div>
                            <h5 className="card-title mb-1">{job.title}</h5>
                            <p className="text-muted mb-2">{job.company}</p>
                            <div className="d-flex flex-wrap gap-5 mb-2">
                             
                              <span className="badge bg-light text-dark">
                                <FaBriefcase className="me-3" />
                                {job.type}
                              </span>
                            </div>
                            <div className="mb-2">
                              <span className="badge gap-2 bg-light text-success">
                                <FaDollarSign className="me-4" />
                                {job.salary}
                              </span>
                            </div>
                            <p className="card-text small text-muted d-none d-md-block">{job.description}</p>
                            <div className="d-flex flex-wrap gap-1 mt-2">
                             
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Posted date and Action buttons */}
                      <div className="col-lg-4 mt-3 mt-lg-0">
                        <div className="d-flex flex-column h-100">
                          <div className="mb-auto">
                            <p className="small text-muted mb-3">Posted {job.posted}</p>
                          </div>
                          <div className="d-flex flex-column gap-2">
                            <button 
                              className={`btn ${job.saved ? 'btn-success' : 'btn-outline-success'} w-100`}
                              onClick={() => handleSaveJob(job.id)}
                            >
                              {job.saved ? 
                                <><FaBookmark className="me-2" /> Saved</> : 
                                <><FaRegBookmark className="me-2" /> Save</>
                              }
                            </button>
                            <button className="btn btn-primary w-100">
                              Apply
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="card border-0 shadow-sm">
                <div className="card-body p-5 text-center">
                  <h5 className="mb-3">No jobs found</h5>
                  <p className="text-muted">Try adjusting your search or filter criteria</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Pagination - Fixed and Functional */}
        {filteredJobs.length > 0 && (
          <div className="row mt-4">
            <div className="col-12">
              <nav aria-label="Job listings pagination">
                <ul className="pagination justify-content-center">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                  </li>
                  
                  {[...Array(totalPages)].map((_, index) => (
                    <li key={index} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => paginate(index + 1)}
                      >
                        {index + 1}
                      </button>
                    </li>
                  ))}
                  
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobBoardPage;